/** HÂRTIA DE CALC pe planșă: imaginea peste care se trasează.
 *
 *  PESTE DESEN, NU SUB EL, și asta e o alegere, nu o scăpare. Planșa noastră e
 *  hârtie OPACĂ — anvelopa se desenează cu fond alb, primul lucru din `Desen` —,
 *  deci o imagine pusă dedesubt n-ar fi ajuns niciodată la ochi. Pusă deasupra,
 *  stinsă la jumătate, face exact ce face calcul pe masa de lucru: se văd
 *  amândouă, iar diferența dintre ele — un zid trasat cu 20 cm mai încolo —
 *  sare în ochi fără să măsoare nimeni nimic.
 *
 *  ȘI E SURDĂ: `pointerEvents="none"` peste tot. O foaie care prinde clickuri ar
 *  fi oprit orice apucare de zid de sub ea, adică tocmai desenatul pentru care
 *  e pusă.
 *
 *  NECALIBRATĂ, SE ÎNCADREAZĂ. Cât nu știe nimeni câți metri are un pixel,
 *  imaginea se așază încadrată în amprenta modulului — nu fiindcă ar fi
 *  adevărat, ci fiindcă altfel n-ar avea unde să stea și n-ai avea ce calibra.
 *  Încadrarea NU se scrie în calc: scara rămâne `0`, adică «nu se măsoară de
 *  aici», și cine se uită la cifre vede că nimeni n-a calibrat încă.
 */
import { useT } from "../../i18n";
import { Icoana } from "./icoane";
import type { Calc } from "./calc";

/** Unde cade imaginea pe planșă, cu tot cu încadrarea provizorie. */
export function asezarea(c: Calc, cadru: { x: number; y: number; w: number; h: number }) {
  if (c.scara > 0) {
    return { scara: c.scara, x0: c.x0, y0: c.y0, rot: c.rot, provizoriu: false };
  }
  const s = Math.min(cadru.w / Math.max(1, c.latime),
                     cadru.h / Math.max(1, c.inaltime));
  return { scara: s, x0: cadru.x, y0: cadru.y, rot: 0, provizoriu: true };
}

export default function Calcul({ calc, cadru }: {
  calc: Calc;
  /** Cadrul planului, pentru încadrarea de dinaintea calibrării. */
  cadru: { x: number; y: number; w: number; h: number };
}) {
  if (calc.ascuns) return null;
  const a = asezarea(calc, cadru);
  return (
    <g pointerEvents="none" opacity={calc.opacitate}
       transform={`translate(${a.x0} ${a.y0}) rotate(${a.rot}) `
                  + `scale(${a.scara})`}>
      <image href={calc.imagine} x={0} y={0}
             width={calc.latime} height={calc.inaltime} />
    </g>
  );
}

/** Bara ei de pe planșă: cât se vede, dacă se vede, și cât e de lată.
 *
 *  LĂȚIMEA ÎN METRI, nu scara în metri pe pixel: «12,40 m» se verifică dintr-o
 *  privire — un plan de apartament are între 8 și 20 —, pe când «0,0052 m/px»
 *  nu spune nimănui nimic. E singura cifră din care se vede că o calibrare a
 *  ieșit prost.
 */
export function BaraCalcului({ calc, laSchimbare, laScoatere }: {
  calc: Calc;
  laSchimbare: (c: Calc) => void;
  laScoatere: () => void;
}) {
  const t = useT();
  const latimeM = calc.scara > 0 ? calc.latime * calc.scara : 0;
  return (
    <div className="ob-ed-calc">
      <Icoana nume="calc" marime={15} />
      <b>{t("calc.titlu")}</b>
      <span className="ob-ed-calc-cifra">
        {calc.scara > 0
          ? t("calc.latime", { m: latimeM.toFixed(2) })
          : t("calc.necalibrat")}
      </span>
      <input type="range" min={10} max={100} step={5}
             value={Math.round(calc.opacitate * 100)}
             title={t("calc.opacitate")}
             onChange={(e) => laSchimbare(
               { ...calc, opacitate: Number(e.target.value) / 100, ascuns: false })} />
      <button className={`ob-ed-icon${calc.ascuns ? "" : " on"}`}
              title={t(calc.ascuns ? "calc.arata" : "calc.ascunde")}
              onClick={() => laSchimbare({ ...calc, ascuns: !calc.ascuns })}>
        <Icoana nume="calc" />
      </button>
      {/* SCOATEREA E A OMULUI, și nu întreabă de două ori: calcul nu e o parte a
          modulului, e o foaie pusă sub mână. Ce s-a trasat după ea rămâne. */}
      <button className="ob-ed-icon" title={t("calc.scoate")}
              onClick={laScoatere}>
        <Icoana nume="inchide" />
      </button>
    </div>
  );
}
